import React from "react";
import ScheduleDetailsModal, {
  DetailItem,
  HeaderItem,
  HighlightCard,
  ScheduleDetailsModalAction,
} from "../../../components/ScheduleDetailsModal/ScheduleDetailsModal";
import { CustomButtonVariant } from "../../../components/CustomButton/CustomButton";
import { Row } from "../../../modules/storage/models/storage-dto";
import {
  convertToDefaultDate,
  formatDateWithoutTimezone,
} from "../../../utils/dateFormatter";
import { getThousand, StatusTypeEnum } from "../../../utils";

interface StorageDetailsModalProps {
  open: boolean;
  onClose: () => void;
  data: Row;
  isDischarge: boolean;
  isRejected?: boolean;
}

function buildHeader(data: Row): HeaderItem[] {
  return [
    {
      label: "Razão social",
      value: data.contract?.customerCompany?.description || "-",
      flex: 1,
    },
    {
      label: "Filial",
      value: data.contract?.subsidiary?.description || "-",
    },
    {
      label: "BP",
      value: data.contract?.customerCompany?.externalId || "-",
    },
    {
      label: "Solicitado por",
      value: data.customerRequester || "-",
    },
  ];
}

function buildHighlightCards(
  data: Row,
  isDischarge: boolean,
  isRejected: boolean
): HighlightCard[] {
  const measurementUnit = data.contract?.product?.measurementUnit || "";

  const volumeItems = [
    {
      label: "Volume solicitado",
      value: `${getThousand(data.volumeRequested)} ${measurementUnit}`,
    },
  ];

  if (!isRejected) {
    volumeItems.push({
      label: "Volume aprovado",
      value: `${getThousand(data.volumeProvided)} ${measurementUnit}`,
    });
  }

  const volumeCard: HighlightCard = { items: volumeItems };

  const scheduleCard: HighlightCard = {
    items: [
      {
        label: isDischarge ? "Data da Programação" : "Data prevista",
        value: convertToDefaultDate(data.scheduleDate),
      },
      {
        label: isDischarge ? "Hora da Programação" : "Hora prevista",
        value:
          data.scheduleTime ||
          formatDateWithoutTimezone(data.scheduleDate, "HH:mm") ||
          "-",
      },
    ],
  };

  return [volumeCard, scheduleCard];
}

function buildDetails(data: Row, isDischarge: boolean): DetailItem[] {
  const statusLabel =
    StatusTypeEnum.find((s) => s.value === data.status)?.label || "-";

  const baseDetails: DetailItem[] = [
    {
      label: "Status",
      value: statusLabel,
    },
    {
      label: "Produto",
      value: data.contract?.product?.description || "-",
    },
    {
      label: "Modalidade",
      value: data.contract?.mod || "-",
    },
    {
      label: "Canal de distribuição",
      value: data.distributionChannel || "-",
    },
    {
      label: "OV SAP",
      value: data.externalId || "-",
    },
  ];

  if (data.sapCorrectedVolume) {
    baseDetails.push({
      label: "Volume OV",
      value: `${getThousand(data.sapCorrectedVolume)} ${
        data.contract?.product?.measurementUnit || ""
      }`,
    });
  }

  if (isDischarge) {
    if (data.orderNumber) {
      baseDetails.push({ label: "Nº da NF", value: data.orderNumber });
    }
    if (data.orderSeries) {
      baseDetails.push({ label: "Série da NF", value: data.orderSeries });
    }
    if (data.unitPrice) {
      baseDetails.push({
        label: "Preço Unitário",
        value: data.unitPrice.toLocaleString("pt-BR", {
          style: "currency",
          currency: "BRL",
          minimumFractionDigits: 6,
        }),
      });
    }
  } else {
    if (data.referenceDocument) {
      baseDetails.push({
        label: "Nº da fatura",
        value: data.referenceDocument,
      });
    }
    if (data.orderNumber && data.orderSeries) {
      baseDetails.push({
        label: "Nº e Série da NF",
        value: `${data.orderNumber}-${data.orderSeries}`,
      });
    }
  }

  return baseDetails;
}

const StorageDetailsModal: React.FC<StorageDetailsModalProps> = ({
  open,
  onClose,
  data,
  isDischarge,
  isRejected = false,
}) => {
  const title = isDischarge
    ? "Detalhes da programação de descarga"
    : "Detalhes da programação de entrega";

  const header = buildHeader(data);
  const highlightCards = buildHighlightCards(data, isDischarge, isRejected);
  const details = buildDetails(data, isDischarge);

  const footer = {
    label: "Observação do cliente",
    value: data.customerRequesterNote || "Nenhuma observação registrada.",
  };

  const actions: ScheduleDetailsModalAction[] = [
    {
      label: "Fechar",
      variant: CustomButtonVariant.CONTAINED,
      onClick: onClose,
    },
  ];

  return (
    <ScheduleDetailsModal
      open={open}
      onClose={onClose}
      title={title}
      header={header}
      highlightCards={highlightCards}
      details={details}
      footer={footer}
      actions={actions}
    />
  );
};

export default StorageDetailsModal;
